import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faAngleDown,
  faBars,
  faXmark,
} from "@fortawesome/free-solid-svg-icons";
import { useEffect, useRef, useState } from "react";
import constants from "../constants/constants";
import { useUserParams } from "../hooks/useUserParams";

export default function MobileHeader({ location, path, adjustHeader, headerRef }) {
  const [isOpen, setIsOpen] = useState(false);
  const [activeMenu, setActiveMenu] = useState(null);
  const { getCurrentLogo } = useUserParams();

  const toggleMenu = (slug) => {
    setActiveMenu((prev) => (prev === slug ? null : slug));
  };

  const drawerVariants = {
    closed: {
      x: "100%",
      transition: {
        duration: 0.45,
        ease: [0.76, 0, 0.24, 1],
        when: "afterChildren",
      },
    },
    open: {
      x: 0,
      transition: {
        duration: 0.45,
        ease: [0.22, 1, 0.36, 1],
        when: "beforeChildren",
        staggerChildren: 0.08,
      },
    },
  };

  const itemVariants = {
    closed: {
      opacity: 0,
      x: 30,
    },
    open: {
      opacity: 1,
      x: 0,
      transition: {
        duration: 0.35,
      },
    },
  };

  return (
    <>
      {/* Header */}
      <header ref={headerRef} className="fixed top-0 left-0 z-40 flex h-24 w-full items-center justify-between px-6">
        <img
          src={getCurrentLogo()}
          alt="Logo"
          className="h-12 object-contain"
        />

        <motion.button
          whileTap={{ scale: 0.9 }}
          whileHover={{ scale: 1.05 }}
          onClick={() => setIsOpen(true)}
          className="flex h-12 w-12 items-center justify-center rounded-full bg-summit-primary/70 backdrop-blur-md"
        >
          <FontAwesomeIcon
            icon={faBars}
            className="text-xl text-white"
          />
        </motion.button>
      </header>

      <AnimatePresence>
        {isOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              onClick={() => setIsOpen(false)}
              className="fixed inset-0 z-40 bg-black/60 backdrop-blur-sm"
            />

            {/* Drawer */}
            <motion.nav
              variants={drawerVariants}
              initial="closed"
              animate="open"
              exit="closed"
              className="fixed top-0 right-0 z-50 flex h-screen w-[85%] max-w-sm flex-col bg-summit-primary px-8 pt-8 shadow-2xl"
            >
              {/* Close */}
              <div className="mb-12 flex items-center justify-between">
                <img
                  src={getCurrentLogo()}
                  alt="Logo"
                  className="h-12"
                />

                <motion.button
                  whileTap={{ scale: 0.9 }}
                  whileHover={{ rotate: 90 }}
                  onClick={() => setIsOpen(false)}
                >
                  <FontAwesomeIcon
                    icon={faXmark}
                    className="text-2xl text-summit-background"
                  />
                </motion.button>
              </div>

              {/* Links */}
              {constants.NAVIGATION.map((item) => {
                if (!item.children) {
                  return (
                    <motion.div key={item.title} variants={itemVariants}>
                      <Link
                        to={item.to}
                        onClick={() => setIsOpen(false)}
                        className="block border-b border-summit-highlight/20 py-5 uppercase tracking-[0.2em] text-summit-background"
                      >
                        {item.title}
                      </Link>
                    </motion.div>
                  );
                }

                return (
                  <motion.div
                    key={item.title}
                    variants={itemVariants}
                    className="border-b border-summit-highlight/20"
                  >
                    {/* Parent Menu */}
                    <button
                      onClick={() => toggleMenu(item.slug)}
                      className="flex w-full items-center justify-between py-5 uppercase tracking-[0.2em] text-summit-background"
                    >
                      <span>{item.title}</span>

                      <FontAwesomeIcon
                        icon={faAngleDown}
                        className={`duration-300 ${activeMenu === item.slug ? "rotate-180" : ""
                          }`}
                      />
                    </button>

                    {/* Submenus */}
                    <AnimatePresence>
                      {activeMenu === item.slug && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.3 }}
                          className="overflow-hidden pb-3"
                        >
                          {item.children.map((child) => (
                            <Link
                              key={child.to}
                              to={child.to}
                              onClick={() => setIsOpen(false)}
                              className="block py-3 pl-6 text-sm uppercase tracking-[0.15em] text-summit-background/80 hover:text-summit-highlight"
                            >
                              {child.title}
                            </Link>
                          ))}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </motion.div>
                );
              })}
            </motion.nav >

          </>
        )}
      </AnimatePresence >
    </>
  );
}